import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ArrowLeft, PlusCircle, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { broadcastApi } from '../../../api';
import useUIStore from '../../../store/useUIStore';
import useConfirm from '../../../hooks/useConfirm';

const BroadcastGroupsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ type: '', message: '' });
  const showSnackbar = useUIStore((state) => state.showSnackbar);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [recipientsDialogOpen, setRecipientsDialogOpen] = useState(false);
  const [recipientInputs, setRecipientInputs] = useState([{ id: null, value: '' }]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [recipientMode, setRecipientMode] = useState('add'); // add | update | delete
  const [recipientsList, setRecipientsList] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientsViewOpen, setRecipientsViewOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const cardSx = {
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    boxShadow: 'none',
  };
  const primaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: `0 10px 20px ${alpha(theme.palette.primary.main, 0.2)}`,
  };
  const secondaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    borderColor: alpha(theme.palette.primary.main, 0.25),
    '&:hover': {
      borderColor: alpha(theme.palette.primary.main, 0.45),
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
  };
  const tableContainerSx = {
    borderRadius: 2,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
  };

  const sanitizePhone = (value = '') => {
    const trimmed = value.trim();
    const digits = trimmed.replace(/\D/g, '');
    return digits;
  };

  const getRecipientValue = (entry) => (typeof entry === 'string' ? entry : entry?.value || '');

  const validateNumbers = (numbers) => {
    const normalizedNumbers = [];
    for (const num of numbers) {
      const normalized = sanitizePhone(num);
      if (!normalized) {
        return { valid: false, message: 'Phone number is required' };
      }
      if (normalized.length < 10 || normalized.length > 15) {
        return { valid: false, message: 'Phone number must be between 10 and 15 digits' };
      }
      normalizedNumbers.push(normalized);
    }
    return { valid: true, numbers: normalizedNumbers };
  };

  const getErrorMessage = (error, fallback) =>
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallback;

  useEffect(() => {
    loadGroups();
  }, []);

  useEffect(() => {
    if (status.type !== 'success' || !status.message) return;
    showSnackbar({ message: status.message, severity: 'success' });
    setStatus({ type: '', message: '' });
  }, [showSnackbar, status]);

  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= groups.length) {
      setPage(0);
    }
  }, [groups.length, page, rowsPerPage]);

  const paginatedGroups = useMemo(() => {
    const start = page * rowsPerPage;
    return groups.slice(start, start + rowsPerPage);
  }, [groups, page, rowsPerPage]);

  const loadGroups = async () => {
    try {
      setLoading(true);
      const response = await broadcastApi.getGroups();
      const payload = response?.data || {};
      const rawList = payload?.data?.groups || payload?.groups || payload?.data || [];
      const list = Array.isArray(rawList) ? rawList : [];
      const normalized = list.map((g) => {
        const count =
          g.recipient_count ??
          g.recipients_count ??
          g.total_recipients ??
          g.recipientCount ??
          (Array.isArray(g.recipients) ? g.recipients.length : 0) ??
          (Array.isArray(g.customers) ? g.customers.length : 0);
        return { ...g, _recipientCount: count || 0 };
      });
      setGroups(normalized);
      setStatus({ type: '', message: '' });
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to load groups');
      setStatus({ type: 'error', message });
      showSnackbar({ message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const openGroupDialog = (group = null) => {
    setEditingGroupId(group?.id || group?.bg_id || null);
    setGroupName(group?.name || group?.bg_name || '');
    setGroupDialogOpen(true);
  };

  const handleSaveGroup = async () => {
    if (!groupName.trim()) {
      setStatus({ type: 'error', message: 'Group name is required' });
      return;
    }
    try {
      if (editingGroupId) {
        await broadcastApi.updateGroup(editingGroupId, { name: groupName.trim() });
        setStatus({ type: 'success', message: 'Group updated successfully' });
      } else {
        await broadcastApi.createGroup({ name: groupName.trim() });
        setStatus({ type: 'success', message: 'Group created successfully' });
      }
      setGroupDialogOpen(false);
      setGroupName('');
      setEditingGroupId(null);
      loadGroups();
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to save group');
      setStatus({ type: 'error', message });
      showSnackbar({ message, severity: 'error' });
    }
  };

  const openRecipientsDialog = async (groupId, mode = 'add') => {
    setSelectedGroupId(groupId);
    setRecipientMode(mode);
    setBulkFile(null);
    setRecipientsDialogOpen(true);

    if (mode === 'update' || mode === 'delete') {
      try {
        setRecipientsLoading(true);
        const response = await broadcastApi.getRecipients(groupId);
        let list = response?.data?.data || response?.data || [];
        if (list && !Array.isArray(list)) {
          list = list.recipients || [];
        }
        const normalized = Array.isArray(list) ? list : [];
        setRecipientsList(normalized);
        if (mode === 'update') {
          const numbers = normalized
            .map((rec) => {
              const phone =
                rec.phone ||
                rec.number ||
                rec.mobile ||
                rec.customer?.phone ||
                rec.customer?.number ||
                rec.customerId ||
                '';
              const value = sanitizePhone(phone);
              return {
                id: rec.id || rec.recipientId || null,
                value,
                originalValue: value,
              };
            })
            .filter((rec) => rec.value);
          setRecipientInputs(numbers.length ? numbers : []);
        }
      } catch (error) {
        const message = error?.response?.data?.message || 'Failed to load recipients';
        setStatus({ type: 'error', message });
        setRecipientInputs([]);
        setRecipientsList([]);
      } finally {
        setRecipientsLoading(false);
      }
    } else {
      setRecipientsList([]);
      setRecipientInputs([{ id: null, value: '' }]);
    }
  };

  const openRecipientsView = async (groupId) => {
    setRecipientsLoading(true);
    setRecipientsViewOpen(true);
    setSelectedGroupId(groupId);
    try {
      const response = await broadcastApi.getRecipients(groupId);
      let list = response?.data?.data || response?.data || [];
      if (list && !Array.isArray(list)) {
        list = list.recipients || [];
      }
      setRecipientsList(Array.isArray(list) ? list : []);
      setStatus({ type: '', message: '' });
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to load recipients';
      setStatus({ type: 'error', message });
      setRecipientsList([]);
    } finally {
      setRecipientsLoading(false);
    }
  };

  const handleDeleteRecipient = async (recipientId, recipientLabel) => {
    if (!selectedGroupId) {
      setStatus({ type: 'error', message: 'No group selected for deletion' });
      return;
    }
    if (!recipientId) {
      setStatus({ type: 'error', message: 'Recipient ID not found' });
      return;
    }
    const confirmText = recipientLabel ? `Delete ${recipientLabel}?` : 'Delete this recipient?';
    const isConfirmed = await confirm({
      title: 'Delete Recipient',
      message: confirmText,
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'error',
    });
    if (!isConfirmed) return;

    try {
      await broadcastApi.deleteRecipient(selectedGroupId, recipientId);
      setRecipientsList((prev) =>
        prev.filter((rec) => (rec.id || rec.recipientId) !== recipientId)
      );
      setStatus({ type: 'success', message: 'Recipient deleted successfully' });
      loadGroups();
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to delete recipient');
      setStatus({ type: 'error', message });
      showSnackbar({ message, severity: 'error' });
    }
  };

  const handleRemoveRecipientInput = async (entry, idx) => {
    if (!selectedGroupId) {
      setStatus({ type: 'error', message: 'No group selected for deletion' });
      return;
    }
    setRecipientInputs((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length ? next : [{ id: null, value: '' }];
    });
  };

  const handleUpdateRecipients = async () => {
    if (!selectedGroupId) {
      setStatus({ type: 'error', message: 'No group selected for update' });
      return;
    }

    const updates = recipientInputs.filter(
      (entry) =>
        entry.id &&
        sanitizePhone(entry.value) &&
        sanitizePhone(entry.value) !== entry.originalValue
    );

    if (!updates.length) {
      setStatus({ type: 'success', message: 'No changes to update' });
      return;
    }

    const nextNumbers = updates.map((entry) => sanitizePhone(entry.value));
    const validation = validateNumbers(nextNumbers);
    if (!validation.valid) {
      setStatus({ type: 'error', message: validation.message });
      return;
    }

    try {
      for (const entry of updates) {
        await broadcastApi.deleteRecipient(selectedGroupId, entry.id);
      }
      await broadcastApi.addRecipients(selectedGroupId, { customer_numbers: validation.numbers });
      setStatus({ type: 'success', message: 'Recipients updated successfully' });
      setRecipientsDialogOpen(false);
      setSelectedGroupId(null);
      setRecipientInputs([{ id: null, value: '' }]);
      loadGroups();
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to update recipients');
      setStatus({ type: 'error', message });
      showSnackbar({ message, severity: 'error' });
    }
  };

  const handleAddRecipients = async () => {
    try {
      if (bulkFile) {
        const formData = new FormData();
        formData.append('file', bulkFile);
        await broadcastApi.uploadRecipientsCsv(selectedGroupId, formData);
        setStatus({ type: 'success', message: 'Recipients uploaded successfully' });
      } else {
        const recipients = recipientInputs
          .map((entry) => sanitizePhone(getRecipientValue(entry)))
          .filter(Boolean);

        if (!recipients.length) {
          setStatus({ type: 'error', message: 'Please enter at least one recipient number' });
          return;
        }

        const validation = validateNumbers(recipients);
        if (!validation.valid) {
          setStatus({ type: 'error', message: validation.message });
          return;
        }

        const payloadNumbers = validation.numbers || recipients;
        await broadcastApi.addRecipients(selectedGroupId, { customer_numbers: payloadNumbers });
        setStatus({ type: 'success', message: 'Recipients added successfully' });
      }

      setRecipientsDialogOpen(false);
      setSelectedGroupId(null);
      setRecipientInputs([{ id: null, value: '' }]);
      setBulkFile(null);
      loadGroups();
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to add recipients');
      setStatus({ type: 'error', message });
      showSnackbar({ message, severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: alpha(theme.palette.primary.main, 0.02), minHeight: '100%' }}>
      <Card sx={{ ...cardSx, mb: 2, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)` }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="outlined"
                  startIcon={<ArrowLeft size={16} />}
                  onClick={() => navigate('/broadcast')}
                  size="small"
                  sx={secondaryButtonSx}
                >
                  Back to Broadcasts
                </Button>
                <Typography variant="h5" fontWeight={700}>Broadcast Groups</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Manage your broadcast groups and add recipients.
              </Typography>
            </Stack>
            <Button
              variant="contained"
              startIcon={<PlusCircle size={16} />}
              onClick={() => openGroupDialog()}
              sx={primaryButtonSx}
            >
              Create Group
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={cardSx}>
        <CardContent>
          {status.message && status.type === 'error' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {status.message}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer sx={tableContainerSx}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                    <TableCell>#</TableCell>
                    <TableCell>Group Name</TableCell>
                    <TableCell>Recipients</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedGroups.map((group, index) => (
                    <TableRow
                      key={group.id || group.bg_id || index}
                      hover
                      sx={{ '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) } }}
                    >
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{group.name || group.bg_name}</TableCell>
                      <TableCell>{group._recipientCount ?? 0}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openRecipientsView(group.id || group.bg_id)}
                          >
                            View Recipients
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openRecipientsDialog(group.id || group.bg_id, 'add')}
                          >
                            Add Recipients
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openRecipientsDialog(group.id || group.bg_id, 'update')}
                          >
                            Update Recipients
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openRecipientsDialog(group.id || group.bg_id, 'delete')}
                          >
                            Delete Recipients
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => openGroupDialog(group)}
                          >
                            Rename group
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {groups.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography align="center" color="text.secondary">
                          No groups available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
        <TablePagination
          component="div"
          count={groups.length}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
        />
      </Card>

      <Dialog
        open={groupDialogOpen}
        onClose={() => setGroupDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editingGroupId ? 'Update Group' : 'Create Group'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Group Name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setGroupDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveGroup}>
            {editingGroupId ? 'Rename group' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={recipientsDialogOpen}
        onClose={() => setRecipientsDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {recipientMode === 'update'
            ? 'Update Recipients'
            : recipientMode === 'delete'
              ? 'Delete Recipients'
              : 'Add Recipients'}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#f8fafc' }}>
          <Stack spacing={2} mt={1}>
            {recipientMode === 'delete' ? (
              recipientsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : recipientsList.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No recipients found for this group.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {recipientsList.map((rec, idx) => {
                    const phone =
                      rec.phone ||
                      rec.number ||
                      rec.mobile ||
                      rec.customer?.phone ||
                      rec.customer?.number ||
                      rec.customerId ||
                      '';
                    const name = rec.name || rec.customer?.name || 'Unknown';
                    const recipientId = rec.id || rec.recipientId;
                    const recipientLabel = phone || name;
                    return (
                      <Box
                        key={recipientId || idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          pb: 1,
                        }}
                      >
                        <Stack spacing={0.2}>
                          <Typography variant="subtitle2">{name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {phone || 'N/A'}
                          </Typography>
                        </Stack>
                        <IconButton
                          size="small"
                          color="error"
                          aria-label="Delete recipient"
                          onClick={() => handleDeleteRecipient(recipientId, recipientLabel)}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Stack>
              )
            ) : (
              <Stack spacing={1.5}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    borderColor: 'divider',
                    background: 'linear-gradient(90deg, rgba(14,165,233,0.06), rgba(99,102,241,0.05))',
                  }}
                >
                  <Typography variant="subtitle2">
                    {recipientMode === 'update' ? 'Update Recipients' : 'Add Recipients'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {recipientMode === 'update'
                      ? 'Edit phone numbers and save changes.'
                      : 'Add phone numbers manually or upload a CSV.'}
                  </Typography>
                </Paper>
                {recipientMode === 'add' && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderColor: 'divider',
                      background: '#fff',
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      Upload recipients
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Upload a CSV with columns like name, phone, email. Only the phone column is required.
                    </Typography>
                    <Button variant="outlined" component="label">
                      {bulkFile ? bulkFile.name : 'Select CSV File'}
                      <input
                        type="file"
                        hidden
                        accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                      />
                    </Button>
                  </Paper>
                )}
                {recipientsLoading && recipientMode === 'update' ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={22} />
                  </Box>
                ) : recipientMode === 'update' && recipientInputs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No recipients found for this group.
                  </Typography>
                ) : (
                  recipientInputs.map((entry, idx) => (
                    <Paper
                      key={idx}
                      variant="outlined"
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        borderColor: 'divider',
                        backgroundColor: '#fff',
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                          label={`Phone ${idx + 1}`}
                          value={getRecipientValue(entry)}
                          onChange={(e) => {
                            const next = [...recipientInputs];
                            next[idx] = { ...next[idx], value: sanitizePhone(e.target.value) };
                            setRecipientInputs(next);
                          }}
                          fullWidth
                          placeholder="9876543210"
                          size="small"
                        />
                        {recipientMode === 'add' && (
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveRecipientInput(entry, idx)}
                            aria-label="Delete recipient"
                            size="small"
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        )}
                      </Stack>
                    </Paper>
                  ))
                )}
                {recipientMode === 'add' && (
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      onClick={() => setRecipientInputs((prev) => [...prev, { id: null, value: '' }])}
                      startIcon={<Plus size={16} />}
                    >
                      Add recipient
                    </Button>
                    {recipientInputs.some((entry) => getRecipientValue(entry).trim()) && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          {recipientInputs.filter((entry) => getRecipientValue(entry).trim()).length} pending
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                )}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setRecipientsDialogOpen(false)}>
            Cancel
          </Button>
          {recipientMode !== 'delete' && (
            <Button
              variant="contained"
              onClick={recipientMode === 'update' ? handleUpdateRecipients : handleAddRecipients}
            >
              {recipientMode === 'update' ? 'Update' : 'Add'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={recipientsViewOpen}
        onClose={() => setRecipientsViewOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Recipients</DialogTitle>
        <DialogContent dividers>
          {recipientsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : recipientsList.length === 0 ? (
            <Typography color="text.secondary">No recipients found for this group.</Typography>
          ) : (
            <Stack spacing={1.5}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1,
                  borderRadius: 1,
                  background: 'linear-gradient(90deg, rgba(14,165,233,0.08), rgba(79,70,229,0.06))',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle2">Recipients</Typography>
                <Typography variant="caption" color="text.secondary">
                  {recipientsList.length} total
                </Typography>
              </Box>
              <Stack spacing={1}>
                {recipientsList.map((rec, idx) => {
                  const phone =
                    rec.phone ||
                    rec.number ||
                    rec.mobile ||
                    rec.customer?.phone ||
                    rec.customer?.number ||
                    rec.customerId ||
                    '';
                  const name = rec.name || rec.customer?.name || 'Unknown';
                  const initial = name?.trim()?.[0]?.toUpperCase?.() || '#';
                  return (
                    <Box
                      key={rec.id || rec.recipientId || idx}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 1.2,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: '#ffffff',
                        boxShadow: '0 6px 18px rgba(15,23,42,0.05)',
                      }}
                    >
                      <Stack direction="row" spacing={1.2} alignItems="center">
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #0ea5e9, #6366f1)',
                            color: 'white',
                            display: 'grid',
                            placeItems: 'center',
                            fontWeight: 700,
                            fontSize: 14,
                          }}
                        >
                          {initial}
                        </Box>
                        <Stack spacing={0.2}>
                          <Typography variant="subtitle2">{name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {phone || 'N/A'}
                          </Typography>
                        </Stack>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        #{idx + 1}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecipientsViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BroadcastGroupsPage;
